// Live multiplayer gameplay shell. Phase machine:
//   shop      → renders <Shop> with multi-store actions
//   battle    → renders <BattleScreen> with the local player's pairing replay
//   between   → brief "round complete" splash; auto-advances on round_start
//   spectator → eliminated overlay + per-pairing battle picker
//   finished  → match-over banner
//
// Status ribbon overlays the shop/spectator views with each player's
// HP/wins/ready state and the current round timer.

import { useEffect, useState, type ReactElement } from 'react'
import { GLYPHS } from '../design-system'
import { useCountdown, formatSeconds } from './useCountdown'
import type {
  GlyphKey,
  LobbyParticipantPublic,
  PlayerProfile,
  SpectatorRoundPayload,
} from '@origami/shared'
import { Shop } from '../game/Shop'
import { type BattleMeta } from './BattleScreen'
import { PixiBattle } from '../pixi/PixiBattleLazy'
import { useMultiStore } from '../multi/multiStore'
import { RotatePrompt, useIsMobile, useIsFoldOpen } from './useIsMobile'
import './MultiPlayScreen.css'

interface MultiPlayScreenProps {
  player: PlayerProfile
  onSessionLost: () => void
  onExitToTitle: () => void
}

export function MultiPlayScreen({
  player,
  onSessionLost,
  onExitToTitle,
}: MultiPlayScreenProps): ReactElement | null {
  const session = useMultiStore((s) => s.session)
  const phase = useMultiStore((s) => s.phase)
  const error = useMultiStore((s) => s.error)
  const loadCatalog = useMultiStore((s) => s.loadCatalog)
  const isMobile = useIsMobile()
  const foldOpen = useIsFoldOpen()

  useEffect(() => {
    void loadCatalog()
  }, [loadCatalog])

  useEffect(() => {
    if (error === 'no_session') onSessionLost()
  }, [error, onSessionLost])

  if (!session) return null

  // Fold reuses the mobile body so the player ribbon collapses to
  // the right-edge `.mp-m-rail` (instead of the top horizontal
  // `.mp-ribbon` that ate too much vertical space inside the
  // ~690px Fold viewport). The Shop / Battle inside PhaseRouter
  // still self-detect Fold via useIsFoldOpen and render their
  // Fold layouts.
  const Body = isMobile || foldOpen ? MobileBody : DesktopBody
  return (
    <div className="paper mp-page">
      {isMobile && <RotatePrompt />}
      <Body player={player} phase={phase} onExitToTitle={onExitToTitle} />
    </div>
  )
}

function DesktopBody({
  player,
  phase,
  onExitToTitle,
}: {
  player: PlayerProfile
  phase: ReturnType<typeof useMultiStore.getState>['phase']
  onExitToTitle: () => void
}): ReactElement {
  const [exitOpen, setExitOpen] = useState(false)
  const requestExit = () => setExitOpen(true)
  return (
    <div className="mp-shell">
      <StatusRibbon />
      <main className="mp-main">
        <PhaseRouter
          player={player}
          phase={phase}
          onExitToTitle={onExitToTitle}
          onRequestExit={requestExit}
        />
      </main>
      {exitOpen && (
        <ExitMatchModal
          onCancel={() => setExitOpen(false)}
          onExitToTitle={onExitToTitle}
        />
      )}
    </div>
  )
}

function MobileBody({
  player,
  phase,
  onExitToTitle,
}: {
  player: PlayerProfile
  phase: ReturnType<typeof useMultiStore.getState>['phase']
  onExitToTitle: () => void
}): ReactElement {
  const [exitOpen, setExitOpen] = useState(false)
  const requestExit = () => setExitOpen(true)
  return (
    // Single-row grid so the fixed-right rail doesn't displace the
    // main area; .mp-m-main reserves its own padding-right to keep
    // cards from sliding under the rail.
    <div className="mp-m-shell">
      <main className="mp-m-main">
        <PhaseRouter
          player={player}
          phase={phase}
          onExitToTitle={onExitToTitle}
          onRequestExit={requestExit}
        />
      </main>
      <MobileMatchRail />
      {exitOpen && (
        <ExitMatchModal
          onCancel={() => setExitOpen(false)}
          onExitToTitle={onExitToTitle}
        />
      )}
    </div>
  )
}

function PhaseRouter({
  player,
  phase,
  onExitToTitle,
  onRequestExit,
}: {
  player: PlayerProfile
  phase: ReturnType<typeof useMultiStore.getState>['phase']
  onExitToTitle: () => void
  /** Opens the exit-confirmation modal. Wired to the Shop's exit
   *  button — clicking it should never silently leave the user
   *  trapped in their match if they meant "abandon and start fresh". */
  onRequestExit: () => void
}): ReactElement | null {
  const session = useMultiStore((s) => s.session)!
  const pendingBattle = useMultiStore((s) => s.pendingBattle)
  const dismissBattle = useMultiStore((s) => s.dismissBattle)
  const matchOver = useMultiStore((s) => s.matchOver)
  const watchingPairingId = useMultiStore((s) => s.watchingPairingId)
  const closeSpectatorReplay = useMultiStore((s) => s.closeSpectatorReplay)
  const spectatorRounds = useMultiStore((s) => s.spectatorRounds)
  const unitsById = useMultiStore((s) => s.unitsById)

  // Spectator watching a chosen pairing replay.
  if (watchingPairingId) {
    const hit = findPairing(spectatorRounds, watchingPairingId)
    if (hit) {
      const meta = buildSpectatorMeta(hit.pairing, hit.round.next_round_at_ms, unitsById)
      return (
        <PixiBattle
          battle={hit.pairing.replay}
          meta={meta}
          onExit={closeSpectatorReplay}
        />
      )
    }
  }

  // Local player's own pairing replay.
  if (phase === 'battle' && pendingBattle) {
    const meta = buildOwnBattleMeta(player, session, pendingBattle, unitsById)
    return (
      <PixiBattle
        battle={pendingBattle.replay}
        meta={meta}
        onExit={dismissBattle}
      />
    )
  }

  if (phase === 'finished') {
    return <FinishedSplash matchOver={matchOver} onExitToTitle={onExitToTitle} />
  }

  if (phase === 'spectator') {
    return (
      <SpectatorPicker
        spectatorRounds={spectatorRounds}
        onRequestExit={onRequestExit}
      />
    )
  }

  if (phase === 'between') {
    return (
      <div className="mp-between">
        <div className="mp-between__inner">
          <h2 className="cute-title">Round complete</h2>
          <p className="hand-note">Folding the next round…</p>
        </div>
      </div>
    )
  }

  // Default: shop. Re-use the production Shop component with multi-store actions.
  return <ShopBody onRequestExit={onRequestExit} />
}

function ShopBody({ onRequestExit }: { onRequestExit: () => void }): ReactElement {
  const session = useMultiStore((s) => s.session)!
  const buy = useMultiStore((s) => s.buy)
  const buyToSlot = useMultiStore((s) => s.buyToSlot)
  const sell = useMultiStore((s) => s.sell)
  const reroll = useMultiStore((s) => s.reroll)
  const combine = useMultiStore((s) => s.combine)
  const reorder = useMultiStore((s) => s.reorder)
  const toggleLock = useMultiStore((s) => s.toggleLock)
  const ready = useMultiStore((s) => s.ready)
  const notice = useMultiStore((s) => s.notice)
  const clearNotice = useMultiStore((s) => s.clearNotice)
  const buffFloaters = useMultiStore((s) => s.buffFloaters)
  const clearBuffFloater = useMultiStore((s) => s.clearBuffFloater)

  return (
    <Shop
      state={session.shop}
      isReady={session.ready}
      notice={notice}
      onClearNotice={clearNotice}
      buffFloaters={buffFloaters}
      onClearFloater={clearBuffFloater}
      actions={{
        onBuy: (slotIndex) => void buy(slotIndex),
        onBuyToSlot: (slotIndex, target, opts) =>
          void buyToSlot(
            slotIndex,
            { where: target.kind, position: target.index },
            opts,
          ),
        onSell: (where, slotIndex) => void sell(where, slotIndex),
        onReroll: () => void reroll(),
        onCombine: (from, to, opts) =>
          void combine(
            { where: from.kind, position: from.index },
            { where: to.kind, position: to.index },
            opts,
          ),
        onMove: (from, to) =>
          void reorder(
            { where: from.kind, position: from.index },
            { where: to.kind, position: to.index },
          ),
        onToggleSlotLock: (slotIndex) => void toggleLock(slotIndex),
        onReady: () => void ready(),
        // Exit shows a modal — abandoning the match is destructive
        // (other players see you removed) so the user picks explicitly
        // between "abandon and leave" vs "leave but keep the session".
        onExit: onRequestExit,
      }}
    />
  )
}

function StatusRibbon({ compact = false }: { compact?: boolean }): ReactElement {
  const session = useMultiStore((s) => s.session)!
  const lobby = session.lobby
  const endsAt = session.round_ends_at_ms
  const remaining = useCountdown(endsAt)
  const aliveParticipants = lobby.participants.filter((p) => !p.is_eliminated)
  const readyCount = aliveParticipants.filter((p) => p.ready).length
  const aliveTotal = aliveParticipants.length
  const localPlayerId = session.lobby.participants.find(
    (p) => p.participant_id === session.participant_id,
  )?.player_id
  const isHost = localPlayerId === lobby.host_player_id
  return (
    <div className={`mp-ribbon ${compact ? 'mp-ribbon--compact' : ''}`}>
      <div className="mp-ribbon__round">
        <span className="tiny-cap">Round</span>
        <strong>{lobby.round_number || '—'}</strong>
      </div>
      <div className="mp-ribbon__chips">
        {lobby.participants.map((p) => (
          <RosterChip
            key={p.participant_id}
            participant={p}
            isYou={p.participant_id === session.participant_id}
            canKick={isHost && !p.ready && !p.is_eliminated && p.participant_id !== session.participant_id}
            compact={compact}
          />
        ))}
      </div>
      {endsAt != null ? (
        <div className="mp-ribbon__timer">
          <span className="tiny-cap">Time</span>
          <strong>{formatSeconds(remaining)}</strong>
        </div>
      ) : (
        // No-timer mode — show ready progress instead of a countdown.
        <div className="mp-ribbon__timer mp-ribbon__timer--manual">
          <span className="tiny-cap">Waiting on ready</span>
          <strong>{readyCount} / {aliveTotal}</strong>
        </div>
      )}
    </div>
  )
}

function RosterChip({
  participant,
  isYou,
  canKick,
  compact,
}: {
  participant: LobbyParticipantPublic
  isYou: boolean
  canKick: boolean
  compact: boolean
}): ReactElement {
  const kick = useMultiStore((s) => s.kick)
  const Glyph = GLYPHS[participant.icon as GlyphKey] ?? GLYPHS.crane!
  const cls = [
    'mp-chip',
    isYou ? 'mp-chip--you' : '',
    participant.is_eliminated ? 'mp-chip--out' : '',
    participant.is_disconnected ? 'mp-chip--disconnected' : '',
    participant.ready ? 'mp-chip--ready' : '',
  ]
    .filter(Boolean)
    .join(' ')
  // Grace countdown ticks down 1/sec from a frozen-at-render
  // disconnect_grace_ms anchor. We don't push fresh
  // participant_state_update every second from the server (that
  // would spam the room); the client extrapolates locally instead.
  const graceAnchor = participant.disconnect_grace_ms != null
    ? Date.now() + participant.disconnect_grace_ms
    : null
  const graceRemaining = useCountdown(graceAnchor)
  return (
    <div className={cls} title={`${participant.display_name} — ${participant.team_name}`}>
      <Glyph size={compact ? 18 : 24} />
      <div className="mp-chip__meta">
        <div className="mp-chip__name">{participant.display_name}</div>
        <div className="mp-chip__stats">
          {participant.is_disconnected
            ? `disconnected · ${graceRemaining}s`
            : `♥ ${participant.health}`}
        </div>
      </div>
      {canKick && (
        <button
          type="button"
          className="mp-chip__kick"
          aria-label={`Kick ${participant.display_name}`}
          title="Mark this player ready so the round can resolve"
          onClick={() => {
            void kick(participant.participant_id)
          }}
        >
          ×
        </button>
      )}
    </div>
  )
}

/** Mobile-landscape replacement for StatusRibbon: a vertical "tally
 *  cord" rail pinned to the right edge of the viewport so it doesn't
 *  consume any of the (already-precious) vertical space the player
 *  cards need. Shows round number, every participant's status
 *  glanceably, ready count, and timer/manual indicator. Tap a tag for
 *  a popover with the player's full info + host kick affordance. */
function MobileMatchRail(): ReactElement {
  const session = useMultiStore((s) => s.session)!
  const kick = useMultiStore((s) => s.kick)
  const lobby = session.lobby
  const endsAt = session.round_ends_at_ms
  const remaining = useCountdown(endsAt)
  const alive = lobby.participants.filter((p) => !p.is_eliminated)
  const readyCount = alive.filter((p) => p.ready).length
  const aliveCount = alive.length
  const localPlayerId = lobby.participants.find(
    (p) => p.participant_id === session.participant_id,
  )?.player_id
  const isHost = localPlayerId === lobby.host_player_id

  // Popover state — anchored to the y-center of the tapped tag so the
  // detail card sits beside the rail at the same vertical level.
  const [popover, setPopover] = useState<{
    participant: LobbyParticipantPublic
    top: number
  } | null>(null)

  // Dismiss popover when the underlying participant disappears or
  // their state changes substantively (e.g. they readied up via /kick
  // and the popover is now stale).
  useEffect(() => {
    if (!popover) return
    const fresh = lobby.participants.find(
      (p) => p.participant_id === popover.participant.participant_id,
    )
    if (!fresh) {
      setPopover(null)
      return
    }
    if (fresh !== popover.participant) {
      setPopover({ ...popover, participant: fresh })
    }
  }, [lobby.participants, popover])

  // Click-outside to dismiss.
  useEffect(() => {
    if (!popover) return
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement
      if (target.closest('.mp-m-popover') || target.closest('.mp-m-tag')) return
      setPopover(null)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [popover])

  return (
    <>
      <aside className="mp-m-rail" aria-label="Match status">
        <div className="mp-m-rail__hanko" aria-label={`Round ${lobby.round_number || '—'}`}>
          R{lobby.round_number || '—'}
        </div>
        <div className="mp-m-rail__cord" aria-hidden />
        <ol className="mp-m-rail__tags">
          {lobby.participants.map((p) => (
            <li key={p.participant_id}>
              <MobileTag
                participant={p}
                isYou={p.participant_id === session.participant_id}
                onTap={(top) => setPopover({ participant: p, top })}
              />
            </li>
          ))}
        </ol>
        <div className="mp-m-rail__cord" aria-hidden />
        <div
          className="mp-m-rail__count"
          aria-label={`${readyCount} of ${aliveCount} ready`}
        >
          {readyCount}/{aliveCount}
        </div>
        <div
          className={`mp-m-rail__mode${
            endsAt == null ? ' mp-m-rail__mode--manual' : ''
          }`}
          aria-label={endsAt == null ? 'Manual ready' : 'Time remaining'}
        >
          {endsAt != null ? formatSeconds(remaining) : '準'}
        </div>
      </aside>

      {popover && (
        <MobileTagPopover
          participant={popover.participant}
          anchorTop={popover.top}
          canKick={
            isHost &&
            !popover.participant.ready &&
            !popover.participant.is_eliminated &&
            popover.participant.participant_id !== session.participant_id
          }
          onKick={() => {
            void kick(popover.participant.participant_id)
            setPopover(null)
          }}
          onClose={() => setPopover(null)}
        />
      )}
    </>
  )
}

function MobileTag({
  participant,
  isYou,
  onTap,
}: {
  participant: LobbyParticipantPublic
  isYou: boolean
  onTap: (anchorTop: number) => void
}): ReactElement {
  const Glyph = GLYPHS[participant.icon as GlyphKey] ?? GLYPHS.crane!
  const cls = [
    'mp-m-tag',
    isYou ? 'mp-m-tag--you' : '',
    participant.is_eliminated ? 'mp-m-tag--out' : '',
    participant.is_disconnected ? 'mp-m-tag--disconnected' : '',
    participant.ready ? 'mp-m-tag--ready' : '',
  ]
    .filter(Boolean)
    .join(' ')
  const state = participant.is_eliminated
    ? 'eliminated'
    : participant.is_disconnected
      ? 'disconnected'
      : participant.ready
        ? 'ready'
        : 'waiting'
  return (
    <button
      type="button"
      className={cls}
      aria-label={`${participant.display_name}: ${state}`}
      onClick={(e) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
        onTap(rect.top + rect.height / 2)
      }}
    >
      <Glyph size={18} />
      {participant.ready && (
        <span className="mp-m-tag__check" aria-hidden>
          ✓
        </span>
      )}
      {participant.is_disconnected && (
        <span className="mp-m-tag__pulse" aria-hidden />
      )}
    </button>
  )
}

function MobileTagPopover({
  participant,
  anchorTop,
  canKick,
  onKick,
  onClose,
}: {
  participant: LobbyParticipantPublic
  anchorTop: number
  canKick: boolean
  onKick: () => void
  onClose: () => void
}): ReactElement {
  // Anchor by viewport top of the tag's centerline. Tighten with
  // clamps so the popover never spills off-screen on a short
  // landscape phone (390px tall).
  const clampedTop = Math.min(
    Math.max(anchorTop - 24, 8),
    (typeof window !== 'undefined' ? window.innerHeight : 600) - 60,
  )
  const grace = participant.disconnect_grace_ms
  return (
    <div
      className="mp-m-popover"
      style={{ top: clampedTop }}
      role="dialog"
      aria-label={`Player details: ${participant.display_name}`}
    >
      <div className="mp-m-popover__meta">
        <div className="mp-m-popover__name">{participant.display_name}</div>
        <div className="mp-m-popover__stats">
          {participant.is_disconnected && grace != null
            ? `disconnected · ${Math.max(0, Math.ceil(grace / 1000))}s`
            : `♥ ${participant.health}`}
        </div>
      </div>
      {canKick ? (
        <button
          type="button"
          className="mp-m-popover__kick"
          aria-label={`Kick ${participant.display_name}`}
          onClick={onKick}
        >
          ×
        </button>
      ) : (
        <button
          type="button"
          className="mp-m-popover__close"
          aria-label="Dismiss"
          onClick={onClose}
        >
          ⌄
        </button>
      )}
    </div>
  )
}

/** Modal shown when the player taps the Shop's Exit button (or the
 *  spectator screen's Exit). Three explicit choices:
 *    - Abandon: server-side abandon + tear down + return to title
 *      (other players see the slot drop out of the pairing pool)
 *    - Keep session: just navigate away; the match keeps running and
 *      coming back to ?multi resumes the player into it
 *    - Cancel: dismiss the modal, stay where you were */
function ExitMatchModal({
  onCancel,
  onExitToTitle,
}: {
  onCancel: () => void
  onExitToTitle: () => void
}): ReactElement {
  const abandon = useMultiStore((s) => s.abandon)
  async function abandonAndExit() {
    await abandon()
    onExitToTitle()
  }
  return (
    <div className="mp-exit-modal" role="dialog" aria-modal="true" aria-labelledby="mp-exit-title">
      <div className="mp-exit-modal__backdrop" onClick={onCancel} />
      <div className="mp-exit-modal__plate">
        <h2 id="mp-exit-title" className="cute-title mp-exit-modal__title">
          Step away from the table?
        </h2>
        <p className="hand-note mp-exit-modal__copy">
          You can leave for now and the match keeps running — you can rejoin
          from the title menu. Or abandon outright and free your seat for the
          rest of the table.
        </p>
        <div className="mp-exit-modal__actions">
          <button
            type="button"
            className="cute-btn mp-exit-modal__cancel"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="cute-btn mp-exit-modal__keep"
            onClick={onExitToTitle}
          >
            Leave (keep session)
          </button>
          <button
            type="button"
            className="cute-btn mp-exit-modal__abandon"
            onClick={() => void abandonAndExit()}
          >
            Abandon match
          </button>
        </div>
      </div>
    </div>
  )
}

function SpectatorPicker({
  spectatorRounds,
  onRequestExit,
}: {
  spectatorRounds: SpectatorRoundPayload[]
  onRequestExit: () => void
}): ReactElement {
  const watch = useMultiStore((s) => s.watchSpectatorPairing)
  const latest = spectatorRounds[spectatorRounds.length - 1]
  return (
    <div className="mp-spectator">
      <div className="mp-spectator__plate">
        <h2 className="cute-title">Your scroll is folded</h2>
        <p className="hand-note">
          Watch the remaining table. Pick a pairing to follow:
        </p>
        <button
          type="button"
          className="cute-btn mp-spectator__exit"
          onClick={onRequestExit}
        >
          ← Exit
        </button>
      </div>
      <div className="mp-spectator__rounds">
        {spectatorRounds.length === 0 && (
          <div className="mp-spectator__empty">Waiting for the next round…</div>
        )}
        {spectatorRounds.map((round) => (
          <section key={round.round_number} className="mp-spectator__round">
            <h3>Round {round.round_number}</h3>
            <div className="mp-spectator__pairings">
              {round.pairings.map((p) => (
                <button
                  key={p.pairing_id}
                  type="button"
                  className="mp-spectator__match"
                  onClick={() => watch(p.pairing_id)}
                >
                  <strong>{p.side_a.display_name}</strong>
                  <span className="mp-spectator__vs">vs</span>
                  <strong>
                    {p.side_b
                      ? p.side_b.display_name
                      : `Ghost of ${p.side_a.display_name}`}
                  </strong>
                  <span className="mp-spectator__winner">
                    {p.winner === 'tie'
                      ? 'tied'
                      : p.winner === 'A'
                        ? `${p.side_a.display_name} won`
                        : `${p.side_b?.display_name ?? 'ghost'} won`}
                  </span>
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
      <div className="mp-spectator__hint hand-note">
        {latest ? `Latest: round ${latest.round_number}` : 'No rounds resolved yet.'}
      </div>
    </div>
  )
}

function FinishedSplash({
  matchOver,
  onExitToTitle,
}: {
  matchOver: ReturnType<typeof useMultiStore.getState>['matchOver']
  onExitToTitle: () => void
}): ReactElement {
  const exitMatch = useMultiStore((s) => s.exitMatch)
  const winner = matchOver?.winner
  return (
    <div className="mp-finished">
      <div className="mp-finished__plate">
        <h1 className="cute-title">{winner ? 'A Triumphant Fold' : 'The Table Tears'}</h1>
        <p className="hand-note mp-finished__sub">
          {winner
            ? `${winner.display_name} (${winner.team_name}) is the last scroll standing.`
            : 'Every scroll was folded — no winner this match.'}
        </p>
        <div className="mp-finished__actions">
          <button
            type="button"
            className="cute-btn"
            onClick={() => {
              exitMatch()
              onExitToTitle()
            }}
          >
            Title Screen
          </button>
        </div>
      </div>
    </div>
  )
}

// ---- Helpers --------------------------------------------------------

// useCountdown + formatSeconds moved to ./useCountdown so BattleScreen
// (which can't import from MultiPlayScreen — would create a cycle)
// can reuse them for the post-battle intermission pill.

function findPairing(
  rounds: SpectatorRoundPayload[],
  pairingId: string,
): { round: SpectatorRoundPayload; pairing: SpectatorRoundPayload['pairings'][number] } | null {
  for (const r of rounds) {
    for (const p of r.pairings) {
      if (p.pairing_id === pairingId) return { round: r, pairing: p }
    }
  }
  return null
}

function buildOwnBattleMeta(
  player: PlayerProfile,
  session: NonNullable<ReturnType<typeof useMultiStore.getState>['session']>,
  pending: NonNullable<ReturnType<typeof useMultiStore.getState>['pendingBattle']>,
  unitsById: Record<
    string,
    { name?: string; glyph?: string; is_flying?: boolean; body_offset_y?: number; body_scale?: number; element?: string; animal?: string }
  >,
): BattleMeta {
  const tagify = (s: string) =>
    '@' + s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  const unitGlyphs: Record<string, string> = {}
  const unitNames: Record<string, string> = {}
  const unitFlying: Record<string, boolean> = {}
  const unitBodyOffsetY: Record<string, number> = {}
  const unitBodyScale: Record<string, number> = {}
  const unitElements: Record<string, string> = {}
  const unitAnimals: Record<string, string> = {}
  for (const [id, def] of Object.entries(unitsById)) {
    if (def.glyph) unitGlyphs[id] = def.glyph
    if (def.name) unitNames[id] = def.name
    if (def.is_flying) unitFlying[id] = true
    if (def.body_offset_y) unitBodyOffsetY[id] = def.body_offset_y
    if (def.body_scale !== undefined && def.body_scale !== 1) unitBodyScale[id] = def.body_scale
    if (def.element) unitElements[id] = def.element
    if (def.animal) unitAnimals[id] = def.animal
  }
  // The simulator decides per-pairing which player is side A vs B. The
  // replay's unit refs (A:1, B:2, ...) are anchored to those sides, so
  // we anchor the labels the same way: whichever side the local player
  // landed on gets their name + the "· you" tag.
  const localRecord = `${session.shop.wins}-${Math.max(0, session.lobby.round_number - 1 - session.shop.wins)}`
  const localTag = tagify(player.display_name)
  const opponentTag = tagify(pending.opponent.display_name)
  const localIsA = pending.local_side === 'A'
  return {
    round: session.lobby.round_number,
    pairing: 1,
    seed: 'live',
    playerA: localIsA
      ? {
          name: player.team_name,
          tag: localTag,
          maxHp: session.shop.maxHealth,
          hp: session.shop.health,
          record: localRecord,
          mascot: player.icon as BattleMeta['playerA']['mascot'],
        }
      : {
          name: pending.opponent.team_name,
          tag: opponentTag,
          maxHp: 30,
          hp: 30,
          record: '—',
          mascot: pending.opponent.icon as BattleMeta['playerA']['mascot'],
        },
    playerB: localIsA
      ? {
          name: pending.opponent.team_name,
          tag: opponentTag,
          maxHp: 30,
          hp: 30,
          record: '—',
          mascot: pending.opponent.icon as BattleMeta['playerB']['mascot'],
        }
      : {
          name: player.team_name,
          tag: localTag,
          maxHp: session.shop.maxHealth,
          hp: session.shop.health,
          record: localRecord,
          mascot: player.icon as BattleMeta['playerB']['mascot'],
        },
    unitGlyphs,
    unitNames,
    unitFlying,
    unitBodyOffsetY,
    unitBodyScale,
    unitElements,
    unitAnimals,
    nextRoundAtMs: pending.next_round_at_ms,
    viewerSide: pending.local_side,
  }
}

function buildSpectatorMeta(
  pairing: SpectatorRoundPayload['pairings'][number],
  nextRoundAtMs: number | null,
  unitsById: Record<
    string,
    { name?: string; glyph?: string; is_flying?: boolean; body_offset_y?: number; body_scale?: number; element?: string; animal?: string }
  >,
): BattleMeta {
  const tagify = (s: string) =>
    '@' + s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  const unitGlyphs: Record<string, string> = {}
  const unitNames: Record<string, string> = {}
  const unitFlying: Record<string, boolean> = {}
  const unitBodyOffsetY: Record<string, number> = {}
  const unitBodyScale: Record<string, number> = {}
  const unitElements: Record<string, string> = {}
  const unitAnimals: Record<string, string> = {}
  for (const [id, def] of Object.entries(unitsById)) {
    if (def.glyph) unitGlyphs[id] = def.glyph
    if (def.name) unitNames[id] = def.name
    if (def.is_flying) unitFlying[id] = true
    if (def.body_offset_y) unitBodyOffsetY[id] = def.body_offset_y
    if (def.body_scale !== undefined && def.body_scale !== 1) unitBodyScale[id] = def.body_scale
    if (def.element) unitElements[id] = def.element
    if (def.animal) unitAnimals[id] = def.animal
  }
  const sideB = pairing.side_b ?? {
    display_name: `Ghost of ${pairing.side_a.display_name}`,
    team_name: 'Echo Team',
    icon: pairing.side_a.icon,
  }
  return {
    round: pairing.replay.event_log[0]?.turn ?? 0,
    pairing: 1,
    seed: 'spec',
    playerA: {
      name: pairing.side_a.team_name,
      tag: tagify(pairing.side_a.display_name),
      maxHp: 30,
      hp: 30,
      record: '—',
      mascot: pairing.side_a.icon as BattleMeta['playerA']['mascot'],
    },
    playerB: {
      name: sideB.team_name,
      tag: tagify(sideB.display_name),
      maxHp: 30,
      hp: 30,
      record: '—',
      mascot: sideB.icon as BattleMeta['playerB']['mascot'],
    },
    unitGlyphs,
    unitNames,
    unitFlying,
    unitBodyOffsetY,
    unitBodyScale,
    unitElements,
    unitAnimals,
    nextRoundAtMs,
    // Spectator — the viewer isn't fighting on either side.
    viewerSide: null,
  }
}
